// HomeFold.jsx — Collapsible wrapper for HomePage sections.
//
// v118.10: HomePage was too long on mobile. Most cards have value buried
// 3-4 screens down. We now wrap each section in a thin collapsible header
// that shows a SHORT summary (icon · title · short stat) and expands the
// full content on click. The user's open/closed state is persisted per
// section in localStorage so they don't have to re-toggle on every reload.
import React, { useState, useEffect, useCallback } from "react";

const STORAGE_PREFIX = "tw_home_fold_";

export default function HomeFold({
  id,
  icon,
  title,
  summary,
  defaultOpen = false,
  alwaysOpen = false,
  testid,
  children,
}) {
  const storageKey = STORAGE_PREFIX + id;
  const [open, setOpen] = useState(() => {
    if (alwaysOpen) return true;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw === "1") return true;
      if (raw === "0") return false;
    } catch (_e) { /* ssr / private mode */ }
    return defaultOpen;
  });

  useEffect(() => {
    if (alwaysOpen) return;
    try { localStorage.setItem(storageKey, open ? "1" : "0"); } catch (_e) { /* ignore */ }
  }, [open, storageKey, alwaysOpen]);

  const toggle = useCallback(() => { if (!alwaysOpen) setOpen((o) => !o); }, [alwaysOpen]);

  return (
    <section
      className="home-fold"
      data-testid={testid || `home-fold-${id}`}
      data-open={open ? "1" : "0"}
      style={{
        marginBottom: 12,
        border: "1px solid var(--border, #e8e2d4)",
        borderRadius: 12,
        background: "var(--card, #fff)",
        overflow: "hidden",
      }}
    >
      <header
        onClick={toggle}
        role={alwaysOpen ? "none" : "button"}
        tabIndex={alwaysOpen ? -1 : 0}
        onKeyDown={(e) => { if (!alwaysOpen && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); toggle(); } }}
        data-testid={`${testid || `home-fold-${id}`}-head`}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          padding: "10px 14px",
          cursor: alwaysOpen ? "default" : "pointer",
          background: open ? "rgba(140,95,50,0.04)" : "transparent",
          transition: "background-color .15s",
          userSelect: "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
          {icon && <span style={{ fontSize: 17 }} aria-hidden>{icon}</span>}
          <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "var(--brown-deep)", lineHeight: 1.2 }}>{title}</div>
            {summary && (
              <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 2, lineHeight: 1.4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {summary}
              </div>
            )}
          </div>
        </div>
        {!alwaysOpen && (
          <span
            aria-hidden
            style={{
              fontSize: 14,
              color: "var(--muted)",
              transition: "transform .2s",
              transform: open ? "rotate(0deg)" : "rotate(-90deg)",
              display: "inline-block",
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            ▼
          </span>
        )}
      </header>
      {open && (
        <div style={{ padding: "0 4px 6px", borderTop: "1px solid var(--border, #e8e2d4)" }}>
          {children}
        </div>
      )}
    </section>
  );
}
